import React, { useState } from 'react';
import { 
    Box, 
    Paper, 
    Typography, 
    TextField, 
    MenuItem, 
    Button, 
    Alert, 
    CircularProgress,
    Grid,
    IconButton
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CancelIcon from '@mui/icons-material/Cancel';
import { apiServer } from '../../api';
import { useLogin } from '../../contexts/LoginContext';

interface IAnexo {
    nomeArquivo: string;
    tipoMime: string;
    conteudoBase64: string;
    previewUrl: string;
}

export default function Sugestao() {
    const [login] = useLogin();
    const [tipo, setTipo] = useState<string>('melhoria');
    const [titulo, setTitulo] = useState<string>('');
    const [descricao, setDescricao] = useState<string>('');
    const [impacto, setImpacto] = useState<string>('medio');
    const [anexos, setAnexos] = useState<IAnexo[]>([]);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [sucesso, setSucesso] = useState<string | null>(null);
    const [erro, setErro] = useState<string | null>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        setErro(null);

        const filesArray = Array.from(e.target.files);
        
        if (anexos.length + filesArray.length > 5) {
            setErro('É permitido anexar no máximo 5 imagens.');
            return;
        }

        filesArray.forEach((file) => {
            if (!file.type.startsWith('image/')) {
                setErro('Apenas arquivos de imagem são permitidos.');
                return;
            }
            if (file.size > 3 * 1024 * 1024) {
                setErro('Cada imagem deve ter no máximo 3MB.');
                return;
            }

            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    const base64String = event.target.result as string;
                    setAnexos((prev) => [
                        ...prev,
                        {
                            nomeArquivo: file.name,
                            tipoMime: file.type,
                            conteudoBase64: base64String,
                            previewUrl: URL.createObjectURL(file)
                        }
                    ].slice(0, 5));
                }
            };
            reader.readAsDataURL(file);
        });
    };

    const removerAnexo = (index: number) => {
        setAnexos((prev) => {
            const novo = [...prev];
            URL.revokeObjectURL(novo[index].previewUrl);
            novo.splice(index, 1);
            return novo;
        });
    };

    const enviarSolicitacao = (e: React.FormEvent) => {
        e.preventDefault();

        if (!titulo.trim() || !descricao.trim()) {
            setErro('Por favor, preencha todos os campos obrigatórios (Título e Descrição).');
            return;
        }

        setCarregando(true);
        setSucesso(null);
        setErro(null);

        const payloadAnexos = anexos.map((a) => ({
            nomeArquivo: a.nomeArquivo,
            tipoMime: a.tipoMime,
            conteudoBase64: a.conteudoBase64
        }));

        apiServer<{ mensagem: string }>()
            .post(
                '/v2/cliente/sugestao', 
                login.accessToken,
                { tipo, titulo, descricao, impacto, anexos: payloadAnexos }
            )
            .then(() => {
                setSucesso('Sua solicitação foi enviada com sucesso! Muito obrigado pelo contato.');
                setTitulo('');
                setDescricao('');
                setTipo('melhoria');
                setImpacto('medio');
                
                // Limpar previews
                anexos.forEach(a => URL.revokeObjectURL(a.previewUrl));
                setAnexos([]);
                
                setCarregando(false);
            })
            .catch((err) => {
                console.error('Erro ao enviar solicitação Fale Conosco:', err);
                const msg = err.response?.data?.mensagem || 'Não foi possível enviar sua solicitação. Tente novamente mais tarde.';
                setErro(msg);
                setCarregando(false);
            });
    };

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto', p: 1 }}>
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <FeedbackIcon color="primary" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Fale Conosco - Suporte & Feedback
                    </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    Use este canal para relatar problemas técnicos/bugs no sistema, enviar comprovantes de pagamento para o financeiro ou sugerir novas funcionalidades e melhorias.
                </Typography>

                {sucesso && <Alert severity="success" sx={{ mb: 3, fontWeight: 500 }}>{sucesso}</Alert>}
                {erro && <Alert severity="error" sx={{ mb: 3, fontWeight: 500 }}>{erro}</Alert>}

                <Box component="form" onSubmit={enviarSolicitacao} noValidate sx={{ mt: 1 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Tipo de Solicitação"
                                value={tipo}
                                onChange={(e) => setTipo(e.target.value)}
                                disabled={carregando}
                            >
                                <MenuItem value="melhoria">Melhoria de Interface / UX</MenuItem>
                                <MenuItem value="nova_funcionalidade">Nova Funcionalidade / Recurso</MenuItem>
                                <MenuItem value="problema_tecnico">Problema Técnico / Bug</MenuItem>
                                <MenuItem value="financeiro">Financeiro / Envio de Comprovante</MenuItem>
                                <MenuItem value="outros">Dúvidas / Outros Assuntos</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label="Impacto Estimado"
                                value={impacto}
                                onChange={(e) => setImpacto(e.target.value)}
                                disabled={carregando}
                                helperText="Como essa solicitação afeta o seu dia a dia?"
                            >
                                <MenuItem value="baixo">Baixo (Ajuste ou refinamento estético)</MenuItem>
                                <MenuItem value="medio">Médio (Facilita ou agiliza processos diários)</MenuItem>
                                <MenuItem value="alto">Alto (Grande diferencial ou resolve um gargalo)</MenuItem>
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                label="Assunto / Título"
                                placeholder="Resuma em poucas palavras o assunto da sua mensagem..."
                                value={titulo}
                                onChange={(e) => setTitulo(e.target.value)}
                                disabled={carregando}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                required
                                fullWidth
                                multiline
                                rows={5}
                                label="Descrição Detalhada"
                                placeholder="Explique em detalhes sua solicitação, descreva o problema técnico ou insira as informações de pagamento..."
                                value={descricao}
                                onChange={(e) => setDescricao(e.target.value)}
                                disabled={carregando}
                            />
                        </Grid>

                        {/* Área de Anexos */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Imagens Anexadas (Máximo 5 imagens - até 3MB cada)
                            </Typography>
                            
                            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                                {/* Botão de Upload */}
                                {anexos.length < 5 && (
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        disabled={carregando}
                                        startIcon={<AddPhotoAlternateIcon />}
                                        sx={{ 
                                            width: 140, 
                                            height: 100, 
                                            borderRadius: 2, 
                                            borderStyle: 'dashed',
                                            textTransform: 'none',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: 1
                                        }}
                                    >
                                        Adicionar
                                        <input
                                            type="file"
                                            hidden
                                            multiple
                                            accept="image/*"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                )}

                                {/* Galeria de Previews */}
                                {anexos.map((anexo, index) => (
                                    <Box 
                                        key={index}
                                        position="relative"
                                        sx={{ 
                                            width: 100, 
                                            height: 100, 
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            overflow: 'hidden',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            backgroundImage: `url(${anexo.previewUrl})`
                                        }}
                                    >
                                        <IconButton
                                            disabled={carregando}
                                            onClick={() => removerAnexo(index)}
                                            size="small"
                                            sx={{ 
                                                position: 'absolute', 
                                                top: 2, 
                                                right: 2, 
                                                bgcolor: 'background.paper',
                                                p: 0.2,
                                                '&:hover': { bgcolor: 'error.main', color: 'white' }
                                            }}
                                        >
                                            <CancelIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>

                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={carregando}
                                startIcon={carregando ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                sx={{ px: 4, py: 1, borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                            >
                                {carregando ? 'Enviando...' : 'Enviar Solicitação'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
}
